import React, { useState, useEffect, useCallback } from 'react';
import { API, ax, card, btn, inp } from './adminHelpers';

function InvoiceRow({ inv, setSelectedInvoice }) {
  const [hovered, setHovered] = useState(false);
  const isPaid = inv.status === 'paid';
  const isExtras = inv.invoice_type === 'extras';

  return (
    <tr 
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ 
        borderBottom: '1px solid rgba(255,255,255,0.04)', 
        transition: 'background 0.2s', 
        background: hovered ? 'rgba(255,255,255,0.02)' : 'transparent' 
      }}
    >
      {/* ID */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#fff', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', padding: '4px 8px', borderRadius: 6 }}>
          INV-{String(inv.id).padStart(5, '0')}
        </span>
      </td>

      {/* Customer Info */}
      <td style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{inv.customer?.name || 'Unknown'}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>{inv.customer?.email}</div>
      </td>

      {/* Booking Details */}
      <td style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 13, color: '#fff' }}>Booking #{inv.booking_id}</div>
        <div style={{ fontSize: 11, color: '#a0a0b8', marginTop: 2 }}>
          {inv.Booking ? `${inv.Booking.booth_type} (${(() => { const [y,m,d] = inv.Booking.event_date.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString(); })()})` : '—'}
        </div>
      </td>

      {/* Invoice Type */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{
          display: 'inline-block',
          background: isExtras ? 'rgba(245,166,35,0.08)' : 'rgba(159,92,255,0.08)',
          color: isExtras ? '#f5a623' : '#9F5CFF',
          border: `1px solid ${isExtras ? 'rgba(245,166,35,0.2)' : 'rgba(159,92,255,0.2)'}`,
          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {inv.invoice_type}
        </span>
      </td>

      {/* Amount */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{ fontSize: 14, color: isPaid ? '#27C85A' : '#f5a623', fontWeight: 700 }}>
          ${parseFloat(inv.amount).toFixed(2)}
        </span>
      </td>

      {/* Sent Date */}
      <td style={{ padding: '16px 18px', fontSize: 12, color: '#e0e0e0' }}>
        {new Date(inv.created_at).toLocaleString()}
      </td>

      {/* Status */}
      <td style={{ padding: '16px 18px' }}>
        <span style={{
          display: 'inline-block',
          background: isPaid ? 'rgba(39,200,90,0.1)' : 'rgba(245,166,35,0.1)',
          border: `1px solid ${isPaid ? 'rgba(39,200,90,0.3)' : 'rgba(245,166,35,0.3)'}`,
          color: isPaid ? '#27C85A' : '#f5a623',
          fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 50, textTransform: 'uppercase'
        }}>
          {inv.status}
        </span>
      </td>

      {/* Actions */}
      <td style={{ padding: '16px 18px' }}>
        <button
          onClick={() => setSelectedInvoice(inv)}
          style={{ ...btn('#9F5CFF', true), fontSize: 11, padding: '6px 12px' }}
        >
          👁 View Items
        </button>
      </td>
    </tr>
  );
}

export default function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const res = await ax().get(`${API}/api/admin/invoices`, { params });
      setInvoices(res.data.invoices || []);
      setTotalPages(res.data.pages || 1);
      setTotalInvoices(res.data.total || 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to load invoices history.');
    } finally {
      setLoading(false);
    }
  }, [page, search, startDate, endDate]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const getInvoiceItems = (items) => {
    let list = items || [];
    if (typeof list === 'string') {
      try { list = JSON.parse(list); } catch (_) { list = []; }
    }
    if (!Array.isArray(list)) return [];
    return list;
  };

  return (
    <div style={{ padding: '4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>🧾 Sent Invoices History</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a0a0b8' }}>
            View and audit all customer booking invoices and extras charges.
          </p>
        </div>
        <div style={{ background: 'rgba(159,92,255,0.1)', border: '1px solid rgba(159,92,255,0.2)', padding: '10px 16px', borderRadius: 12 }}>
          <span style={{ fontSize: 11, color: '#9F5CFF', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, display: 'block' }}>Total Invoices</span>
          <span style={{ fontSize: 18, color: '#fff', fontWeight: 800 }}>{totalInvoices} issued</span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Search Customer</label>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by customer name or email…"
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Start Date</label>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>End Date</label>
          <input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }}
            style={inp(false)} />
        </div>
        {(search || startDate || endDate) && (
          <button onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); setPage(1); }}
            style={{ ...btn('#555', true), padding: '10px 16px', borderRadius: 10, alignSelf: 'flex-end', height: 40 }}>
            Reset
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 14, background: 'rgba(227,67,100,0.1)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 12, marginBottom: 20, color: '#E34364', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#a0a0b8', fontSize: 14 }}>Loading invoice records…</div>
      ) : invoices.length === 0 ? (
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 40, margin: '0 0 12px' }}>🧾</p>
          <p style={{ color: '#a0a0b8', fontSize: 14, fontWeight: 600, margin: 0 }}>No invoices generated yet.</p>
          <p style={{ color: '#555', fontSize: 12, margin: '6px 0 0' }}>Invoices will appear here once bookings or extra items are dispatched.</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 800 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
                    {['Invoice ID', 'Customer Info', 'Booking Details', 'Type', 'Amount', 'Date Sent', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '14px 18px', fontSize: 11, fontWeight: 700, color: '#a0a0b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <InvoiceRow key={inv.id} inv={inv} setSelectedInvoice={setSelectedInvoice} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, padding: '0 4px' }}>
              <span style={{ fontSize: 12, color: '#a0a0b8' }}>Page {page} of {totalPages}</span>
              <div style={{ display: 'flex', gap: 10 }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: page === 1 ? '#222' : '#9F5CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: page === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                  ← Previous
                </button>
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ background: page === totalPages ? '#222' : '#9F5CFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: page === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Details Modal */}
      {selectedInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#101428', borderRadius: 20, padding: 28, border: '1px solid rgba(159,92,255,0.3)', width: '100%', maxWidth: 520, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 700 }}>Invoice Details</h3>
                <p style={{ margin: '4px 0 0', color: '#9F5CFF', fontSize: 12, fontWeight: 600 }}>
                  INV-{String(selectedInvoice.id).padStart(5, '0')} · {selectedInvoice.invoice_type.toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                style={{ background: 'none', border: 'none', color: '#a0a0b8', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {/* Meta */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: 12, padding: 14, marginBottom: 20, fontSize: 13 }}>
              <div>
                <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Customer</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>{selectedInvoice.customer?.name}</span>
                <span style={{ color: '#a0a0b8', display: 'block', fontSize: 11, marginTop: 1 }}>{selectedInvoice.customer?.email}</span>
              </div>
              <div>
                <span style={{ color: '#555', display: 'block', fontSize: 10, textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Booking Info</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>ID #{selectedInvoice.booking_id}</span>
                <span style={{ color: '#a0a0b8', display: 'block', fontSize: 11, marginTop: 1 }}>
                  {selectedInvoice.Booking?.booth_type} ({selectedInvoice.Booking ? (() => { const [y,m,d] = selectedInvoice.Booking.event_date.split('-').map(Number); return new Date(y, m-1, d).toLocaleDateString(); })() : '—'})
                </span>
              </div>
            </div>

            {/* Items */}
            <p style={{ margin: '0 0 10px', fontSize: 11, color: '#a0a0b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Itemized Breakdown</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', marginBottom: 20 }}>
              {getInvoiceItems(selectedInvoice.items).length === 0 ? (
                <p style={{ color: '#555', fontSize: 13, fontStyle: 'italic', margin: 0 }}>No details available for this invoice.</p>
              ) : (
                getInvoiceItems(selectedInvoice.items).map((it, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', borderRadius: 10, padding: '10px 14px' }}>
                    <div>
                      <div style={{ fontSize: 13, color: '#e0e0e0', fontWeight: 600 }}>{it.name || 'Custom Item'}</div>
                      <div style={{ fontSize: 11, color: '#555', marginTop: 1 }}>
                        Qty: {it.quantity || 1} &nbsp;@&nbsp; ${parseFloat(it.price || it.unit_price || 0).toFixed(2)}/ea
                      </div>
                    </div>
                    <span style={{ fontSize: 14, color: '#fff', fontWeight: 700 }}>
                      ${parseFloat(it.total || (it.price || it.unit_price || 0) * (it.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Total / Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: 'rgba(159,92,255,0.08)', borderRadius: 12, border: '1px solid rgba(159,92,255,0.2)', marginBottom: 24 }}>
              <div>
                <span style={{ fontSize: 10, color: '#9F5CFF', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 2 }}>Status</span>
                <span style={{ fontSize: 14, color: selectedInvoice.status === 'paid' ? '#27C85A' : '#f5a623', fontWeight: 700, textTransform: 'uppercase' }}>
                  {selectedInvoice.status}
                </span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 10, color: '#9F5CFF', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 2 }}>Total Amount</span>
                <span style={{ fontSize: 18, color: '#fff', fontWeight: 800 }}>${parseFloat(selectedInvoice.amount).toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={() => setSelectedInvoice(null)}
              style={{ ...btn('#555', true), width: '100%', padding: '12px 0', borderRadius: 10, fontSize: 13 }}
            >
              Close Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
